<script>
import { SEGMENTATION_SETTINGS_SCHEMA } from '@src/components/segmentation/segmentation-typing';

import { useAppSettings } from '@src/composables/useAppSettings';
import { onMounted, reactive, ref } from 'vue';

export default {
  setup() {
    const {
      segmentationSettingsInternal,
      patientHeaderInfo,
      patientHeaderOptions,
      selectedHeaderKeys,
      themeMode,
      networkCompression,
      loadSettings,
      saveSettings: persistSettings,
      takeSnapshot,
      restoreSnapshot,
    } = useAppSettings();

    const isOpen = ref(false);
    const activeSection = ref('viewer');

    // Initialize settings with default values (viewer-level only – not persisted yet)
    const settings = reactive({
      viewer: {
        defaultWindowing: 'DICOM',
        defaultZooming: '200%',
      },
      segmentation: SEGMENTATION_SETTINGS_SCHEMA,
      theme: {
        mode: themeMode,
      },
      network: {
        compression: networkCompression,
      },
    });

    // Setting sections for the menu
    const settingSections = [
      { title: 'Viewer Settings', value: 'viewer', icon: 'mdi-eye' },
      { title: 'Patient Information', value: 'patient', icon: 'mdi-account' },
      {
        title: 'Segmentation',
        value: 'segmentation',
        icon: 'mdi-image-filter-frames',
      },
      { title: 'Theme', value: 'theme', icon: 'mdi-palette' },
      { title: 'Network', value: 'network', icon: 'mdi-connection' },
    ];

    // Sample options for select inputs
    const windowingOptions = ['DICOM'];
    const zoomingOptions = ['200%'];

    const open = () => {
      takeSnapshot();
      isOpen.value = true;
    };

    const cancel = () => {
      restoreSnapshot();
      isOpen.value = false;
    };

    const saveSettings = async () => {
      await persistSettings();
      isOpen.value = false;
    };

    onMounted(async () => {
      await loadSettings();
    });

    return {
      isOpen,
      activeSection,
      settingSections,
      settings,
      windowingOptions,
      zoomingOptions,
      open,
      cancel,
      saveSettings,
      segmentationSettingsInternal,
      patientHeaderInfo,
      patientHeaderOptions,
      selectedHeaderKeys,
    };
  },
};
</script>

<template>
  <v-dialog v-model="isOpen" max-width="900px" max-height="80vh">
    <v-card class="d-flex flex-column" height="70vh">
      <v-card-title class="custom-headline flex-shrink-0">
        App Settings
      </v-card-title>

      <!-- Main content area -->
      <div class="d-flex flex-grow-1" style="min-height: 0">
        <!-- Left sidebar menu -->
        <div
          class="settings-menu flex-shrink-0"
          style="
            width: 250px;
            border-right: 1px solid var(--md-outline-variant);
          "
        >
          <v-list density="compact">
            <v-list-item
              v-for="section in settingSections"
              :key="section.value"
              :value="section.value"
              :active="activeSection === section.value"
              class="mb-1"
              @click="activeSection = section.value"
            >
              <template #prepend>
                <v-icon :icon="section.icon" size="small" />
              </template>
              <v-list-item-title>{{ section.title }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </div>

        <!-- Right content area -->
        <div class="flex-grow-1 d-flex flex-column" style="min-height: 0">
          <v-card-text
            class="flex-grow-1"
            style="overflow-y: auto; min-height: 0"
          >
            <!-- Viewer Settings -->
            <div v-if="activeSection === 'viewer'">
              <h3 class="mb-4">
                Viewer Settings
              </h3>
              <v-select
                v-model="settings.viewer.defaultWindowing"
                label="Default Windowing"
                :items="windowingOptions"
                variant="outlined"
                class="mb-4"
              />
              <v-select
                v-model="settings.viewer.defaultZooming"
                label="Default Zooming"
                :items="zoomingOptions"
                variant="outlined"
                class="mb-4"
              />
            </div>

            <!-- Patient Information -->
            <div v-if="activeSection === 'patient'">
              <h3 class="mb-4">
                Patient Information
              </h3>
              <v-select
                v-model="selectedHeaderKeys"
                label="Patient Information to Show"
                :items="patientHeaderOptions"
                item-title="label"
                item-value="key"
                multiple
                chips
                variant="outlined"
                class="mb-4"
                return-object
              >
                <template #chip="{ item }">
                  <v-chip>{{ item.label }}</v-chip>
                </template>
              </v-select>
            </div>

            <!-- Segmentation -->
            <div v-if="activeSection === 'segmentation'">
              <h3 class="mb-4">
                Segmentation
              </h3>
              <div
                v-for="(settingOption, key) in settings.segmentation"
                :key="key"
              >
                <v-select
                  v-model="segmentationSettingsInternal[key]"
                  :label="settingOption.label"
                  :items="settingOption.optionLabels"
                  variant="outlined"
                  class="mb-4"
                />
              </div>
            </div>

            <!-- Theme -->
            <div v-if="activeSection === 'theme'">
              <h3 class="mb-4">
                Theme
              </h3>
              <v-radio-group v-model="settings.theme.mode" label="Theme Mode">
                <v-radio label="Light" value="light" />
                <v-radio label="Dark" value="dark" />
              </v-radio-group>
            </div>
            <!-- Network -->
            <div v-if="activeSection === 'network'">
              <h3 class="mb-4">
                Network
              </h3>
              <v-switch
                v-model="settings.network.compression"
                label="Use compression for data transfer"
                color="primary"
                hide-details
                class="mb-2"
              />
            </div>
          </v-card-text>

          <v-card-actions class="flex-shrink-0">
            <v-spacer />
            <v-btn color="primary" variant="text" disabled @click="cancel">
              Reset to Default
            </v-btn>

            <v-btn color="primary" variant="text" @click="cancel">
              Cancel
            </v-btn>
            <v-btn color="primary" variant="elevated" @click="saveSettings">
              Save
            </v-btn>
          </v-card-actions>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped>
* {
  color: var(--md-text-primary);
}
.custom-headline {
  background-color: var(--md-surface-container);
  border-bottom: 1px solid var(--md-outline-variant);
  padding: 16px 24px;
}

.settings-menu {
  background-color: var(--md-bg);
}

.v-list-item--active {
  background-color: var(--md-bg);
  color: var(--md-on-primary-container);
}

.v-card,
.v-list {
  background-color: var(--md-bg);
}
</style>
